import { api } from './api';
import { BACKEND_URL } from './config';
import { getAccessToken } from './session';

/* Data access for the structured-curriculum admin view (EDU-66). Reads the */
/* extraction pipeline state (documents.curriculum_status), the persisted */
/* sliding-window payloads (curriculum_extractions, incl. schema_friction — the */
/* "challenge the model" signal), and the materialized programme tree. Triggers */
/* the next extraction window via the extract-curriculum edge function (admin JWT). */

export type CurriculumStatus = 'pending' | 'extracted' | 'skipped' | 'error';

export interface CurriculumOverview {
  cycles: number;
  subjects: number;
  levels: number;
  byStatus: Record<CurriculumStatus, number>;
  totalDocs: number;
  programmes: number;
  themes: number;
  items: number;
  windows: number;
  materializedDocs: number;   /* docs with ≥1 programme (Stage B done) */
  unmaterialized: number;     /* extracted docs still awaiting Stage B */
}

export interface ExtractionRow {
  document_id: string;
  title: string | null;
  page_start: number;
  page_end: number;
  status: 'ok' | 'empty' | 'error';
  item_count: number;
  schema_friction: string | null;
  created_at: string;
}

export interface ProgrammeRow {
  id: string;
  status: string;
  voie: string | null;
  bo_reference: string | null;
  created_at: string;
  subject: string | null;
  cycle: string | null;
  doc_title: string | null;
  theme_count: number;
  item_count: number;
}

/* CLEAN-ARCH (Vague 4) : via le port tRPC admin (counts + agrégations backend). */
export async function fetchOverview(): Promise<CurriculumOverview> {
  return (await api.curriculum.overview.query()) as CurriculumOverview;
}

/* Stage B: list `extracted` documents that don't yet have a programme, and run */
/* materialize_curriculum (deterministic plpgsql RPC) on each. Returns a summary. */
export async function materializeExtracted(): Promise<{ processed: number; themes: number; items: number; skipped: number; refuses?: number; motif?: string }> {
  return api.curriculum.materializeExtracted.mutate();
}

export async function fetchRecentExtractions(limit = 40): Promise<ExtractionRow[]> {
  /* CLEAN-ARCH (Vague 4) : via le port tRPC admin (query + mapping côté backend). */
  return (await api.curriculum.recentExtractions.query({ limit })) as ExtractionRow[];
}

export async function fetchProgrammes(limit = 50): Promise<ProgrammeRow[]> {
  /* CLEAN-ARCH (Vague 4) : via le port tRPC admin (query + mapping côté backend). */
  return (await api.curriculum.programmes.query({ limit })) as ProgrammeRow[];
}

export interface ExtractResult {
  done?: boolean;
  document_id?: string;
  title?: string;
  page_start?: number;
  page_end?: number;
  more_windows?: boolean;
  is_programme?: boolean;
  subject_code?: string;
  cycle_code?: string;
  theme_count?: number;
  item_count?: number;
  schema_friction?: string;
  status?: string;
  message?: string;
  error?: string;
  detail?: string;
}

/* Trigger one extraction window. Pass a document_id to advance that doc, or omit */
/* to let the server pick the next pending document. */
export async function runExtractionWindow(documentId?: string): Promise<ExtractResult> {
  /* CLEAN-ARCH (Vague 4) : route backend (Vertex) au lieu de la fn edge. */
  const token = await getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${BACKEND_URL}/curriculum/extract`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(documentId ? { document_id: documentId } : {}),
  });
  if (!res.ok) throw new Error(`curriculum/extract ${res.status}`);
  return (await res.json()) as ExtractResult;
}

/* ── CRUD : référentiel (cycles / subjects / levels) ──────────────────────── */
export interface RefRow { id: string; code: string; label: string; position: number; nature?: string; cycle_id?: string | null }

/* CLEAN-ARCH (Vague 4) : tables de référence via les ports tRPC admin. */
export async function listRef(table: 'cycles' | 'subjects' | 'levels'): Promise<RefRow[]> {
  return (await api.curriculum.ref.list.query({ table })) as RefRow[];
}
export async function saveRef(table: 'cycles' | 'subjects' | 'levels', row: Partial<RefRow>): Promise<void> {
  await api.curriculum.ref.save.mutate({ table, row });
}
export async function deleteRef(table: 'cycles' | 'subjects' | 'levels', id: string): Promise<void> {
  await api.curriculum.ref.delete.mutate({ table, id });
}

/* ── CRUD : arbre programme → thèmes → items ──────────────────────────────── */
export const ITEM_KINDS = ['attendu', 'connaissance', 'competence', 'exemple', 'automatisme', 'repere', 'notion', 'objet_etude'] as const;
export type ItemKind = typeof ITEM_KINDS[number];

export interface ThemeRow { id: string; code: string | null; title: string; intro: string | null; position: number; pages: number[] }
export interface ItemRow { id: string; theme_id: string | null; kind: string; content: string; source_label: string | null; progression: { kind?: string; levels?: string[] } | null; position: number; pages: number[] }
export interface ProgrammeTree {
  programme: { id: string; status: string; voie: string | null; bo_reference: string | null; is_authoritative: boolean; subject: string | null; cycle: string | null; doc_title: string | null; source_document_id: string | null };
  themes: Array<ThemeRow & { items: ItemRow[] }>;
}

export async function fetchProgrammeTree(programmeId: string): Promise<ProgrammeTree> {
  /* CLEAN-ARCH (Vague 4) : via le port tRPC admin (query + assemblage backend). */
  return (await api.curriculum.tree.query({ programmeId })) as ProgrammeTree;
}

/* Create (completes CRUD: C in addition to U/D). */
export async function createTheme(programmeId: string, title: string): Promise<void> {
  await api.curriculum.createTheme.mutate({ programmeId, title });
}
export async function createItem(programmeId: string, themeId: string, kind: string, content: string): Promise<void> {
  await api.curriculum.createItem.mutate({ programmeId, themeId, kind, content });
}

/* Re-run Stage B for a document (rebuilds its programmes). Note: programme ids change. */
export async function materializeDocument(documentId: string): Promise<{ programmes?: number; items?: number; status?: string; motif?: string; avertissement?: string }> {
  return api.curriculum.materializeDocument.mutate({ documentId });
}

/* CLEAN-ARCH (Vague 4) : mutations programme/thème/item via les ports tRPC admin. */
export async function updateProgramme(id: string, fields: { status?: string; voie?: string | null; bo_reference?: string | null; is_authoritative?: boolean }): Promise<void> {
  await api.curriculum.updateProgramme.mutate({ id, fields });
}
export async function deleteProgramme(id: string): Promise<void> {
  await api.curriculum.deleteProgramme.mutate({ id });
}
export async function updateTheme(id: string, fields: { title?: string; code?: string | null; intro?: string | null }): Promise<void> {
  await api.curriculum.updateTheme.mutate({ id, fields });
}
export async function deleteTheme(id: string): Promise<void> {
  await api.curriculum.deleteTheme.mutate({ id });
}
export async function updateItem(id: string, fields: { kind?: string; content?: string; source_label?: string | null; progression?: Record<string, unknown> | null }): Promise<void> {
  await api.curriculum.updateItem.mutate({ id, fields });
}
export async function deleteItem(id: string): Promise<void> {
  await api.curriculum.deleteItem.mutate({ id });
}

export type VerdictCouverture = 'couvert' | 'range_ailleurs' | 'manquant';

export interface CouvertureDocumentRow {
  id: string | null;
  title: string | null;
  nature: string | null;
  items: number;
}

export interface CouvertureCoupleRow {
  subject_code: string;
  subject_label: string;
  cycle_code: string;
  cycle_label: string;
  voie: string | null;
  language_code: string | null;
  verdict: VerdictCouverture;
  voie_reelle: string | null;
  programmes: number;
  items: number;
  autorite_sans_programme: boolean;
  en_conflit: boolean;
  coordonnee_sans_axe: boolean;
  documents: CouvertureDocumentRow[];
}

export interface ProgrammeInvisibleRow {
  programme_id: string;
  subject_label: string;
  voie: string | null;
  document_id: string | null;
  document_title: string | null;
  document_nature: string | null;
  items: number;
  coupe_en_deux: boolean;
  rematerialisation_bloquee: boolean;
  rematerialisation_motif: string | null;
}

export interface CleInexploitableRow {
  subject_code: string | null;
  cycle_code: string | null;
  voie: string | null;
  champs: string[];
  fenetres: number;
  documents: number;
}

export interface PerimetresCorpus {
  documents: number;
  programmes: number;
  programmes_autoritaires: number;
  items: number;
  fenetres: number;
  pages: number;
  couples: number;
  items_ressuscitables: number;
}

export interface CouvertureCorpus {
  perimetres: PerimetresCorpus;
  couples: CouvertureCoupleRow[];
  invisibles: ProgrammeInvisibleRow[];
  cles_inexploitables: CleInexploitableRow[];
  totaux: {
    couples: number;
    couverts: number;
    ranges_ailleurs: number;
    manquants: number;
    autorite_sans_programme: number;
    couples_en_conflit: number;
    couples_sans_axe: number;
    items_sans_axe: number;
    programmes_sans_cycle: number;
    items_invisibles: number;
    programmes_coupes_en_deux: number;
    items_coupes_en_deux: number;
  };
}

export async function fetchCouverture(): Promise<CouvertureCorpus> {
  return (await api.curriculum.couverture.query()) as CouvertureCorpus;
}

export type MotifLecture =
  | 'programme_sterile'
  | 'pages_jamais_lues'
  | 'nature_inconnue_sans_contenu'
  | 'fenetres_en_echec'
  | 'ignore'
  | 'rejet_probable';

export type ActionLecture = 'extraire' | 'rematerialiser' | 'aucune';

export interface DocumentLectureRow {
  document_id: string;
  title: string | null;
  nature: string | null;
  curriculum_status: string;
  page_count: number;
  pages_lues: number;
  pages_jamais_lues: number;
  fenetres: number;
  fenetres_en_echec: number;
  programmes: number;
  items: number;
  motifs: MotifLecture[];
  demande_attention: boolean;
  rematerialisation_bloquee: boolean;
  rematerialisation_motif: string | null;
  action: ActionLecture;
  couples_touches: number;
  couples_non_jugeables: number;
}

export interface LectureCorpus {
  documents: DocumentLectureRow[];
  totaux: {
    documents_en_attention: number;
    programme_sterile: number;
    nature_inconnue_sans_contenu: number;
    documents_pages_jamais_lues: number;
    pages_jamais_lues: number;
    documents_fenetres_en_echec: number;
    fenetres_en_echec: number;
    ignores: number;
    rejets_probables: number;
  };
}

export async function fetchLecture(): Promise<LectureCorpus> {
  return (await api.curriculum.lecture.query()) as LectureCorpus;
}

export type PerteMaterialisation = 'partielle' | 'totale';

export interface DocumentPerteRow {
  document_id: string;
  title: string | null;
  nature: string | null;
  programmes: number;
  themes_perdus: number;
  items_perdus: number;
  themes_sans_items: number;
  sujets_non_resolus: string[];
  perte: PerteMaterialisation;
}

export interface PerteMaterialisationCorpus {
  documents: DocumentPerteRow[];
  totaux: {
    documents: number;
    partielle_documents: number;
    partielle_themes: number;
    partielle_items: number;
    partielle_documents_programme: number;
    partielle_items_programme: number;
    totale_documents: number;
    totale_themes: number;
    totale_items: number;
    themes_perdus: number;
    items_perdus: number;
    themes_sans_items: number;
  };
}

export async function fetchPerteMaterialisation(): Promise<PerteMaterialisationCorpus> {
  return (await api.curriculum.perteMaterialisation.query()) as PerteMaterialisationCorpus;
}

export interface MessageMaterialisation {
  erreur: string | null;
  note: string | null;
}

/* LA DÉCISION D'AFFICHAGE, EXTRAITE PARCE QU'ELLE ÉTAIT ÉCRITE DEUX FOIS.
   Les deux boutons de re-matérialisation de /admin/couverture portaient le même
   gestionnaire, copié. #1097 en a corrigé un seul : le refus restait affiché
   comme un succès sur l'autre. Ce n'est pas une inattention, c'est ce que
   produit une logique dupliquée — la corriger une fois n'en corrige qu'une.

   Elle vit ici, nommée et testable, plutôt que dans une fermeture JSX : mes
   quatre tests de #1097 portaient sur le garde du domaine et passaient même
   quand on retirait ce code-ci. Un test qui ne peut pas voir le code fautif
   n'est pas un garde. */
export function messageMaterialisation(r: {
  programmes?: number;
  items?: number;
  status?: string;
  motif?: string;
  avertissement?: string;
}): MessageMaterialisation {
  if (r.status === 'refuse') {
    return { erreur: r.motif ?? 'Matérialisation refusée par le garde.', note: null };
  }
  return {
    erreur: null,
    note:
      `Re-matérialisation : ${r.programmes ?? 0} programme(s), ${r.items ?? 0} item(s)` +
      (r.status ? ` · ${r.status}` : '') +
      (r.avertissement ? ` — ${r.avertissement}` : ''),
  };
}

export interface TitreEnDesaccordRow {
  programme_id: string;
  document_id: string;
  title: string;
  subject_code: string;
  subject_label: string;
  items: number;
  titre_evoque: string;
  titre_evoque_label: string;
  suspicion_faible: boolean;
}

export async function fetchTitreEnDesaccord(): Promise<TitreEnDesaccordRow[]> {
  return (await api.curriculum.titreEnDesaccord.query()) as TitreEnDesaccordRow[];
}

export interface ApercuMasse {
  eligibles: number;
  autorises: number;
  bloques: number;
  non_jugeables: number;
}

export async function fetchApercuMasse(): Promise<ApercuMasse> {
  return (await api.curriculum.apercuMasse.query()) as ApercuMasse;
}
